// How the cart presents the backend's verdict on a submitted coupon. Nothing
// here decides whether a code is valid or what it takes off: POST /cart/quote
// evaluates the code for this cart, location and channel and returns
//
//   couponStatus  { code, applied, reason }   (null when no code was evaluated)
//   issues[]      { type: 'coupon_invalid', code, reason, message } on refusal
//   checkoutIncentives[] with the backend's own discountAmount per applied offer
//
// These helpers only choose the words.

// Reasons whose backend wording is generic or names internals ("your outlet")
// get a customer-facing sentence here. Reasons whose backend message carries
// evaluated specifics (the minimum spend, the units still missing, why a better
// offer won) keep the backend's message.
const REASON_MESSAGES = {
  not_found: "This code isn't valid for your cart right now.",
  expired: 'This code has expired.',
  limit_reached: 'This code has reached its usage limit.',
  first_order_only: 'This code is only for a first order.',
  location_required: 'Choose a delivery location we serve to use a code.',
  better_referral_discount: 'Your referral reward saves you more, so it was used instead of this code.',
};

const REASON_FALLBACKS = {
  min_order_value: "Your cart doesn't reach this code's minimum order yet.",
  min_quantity: 'Add more eligible items to use this code.',
  not_applicable: "This code doesn't apply to anything in your cart.",
};

/** The customer-facing sentence for a refused code. */
export const couponRefusalMessage = (reason, backendMessage) => {
  if (REASON_MESSAGES[reason]) return REASON_MESSAGES[reason];
  if (backendMessage) return backendMessage;
  return REASON_FALLBACKS[reason] || "This code couldn't be applied to your cart.";
};

/**
 * The backend's verdict on `couponCode` in `quote`.
 *
 *   { state: 'applied', savings, freeDelivery }  the quote applied the code
 *   { state: 'refused', reason, message }        the quote evaluated and refused it
 *   { state: 'unchecked' }                       the quote did not evaluate it
 *                                                (e.g. nothing in the cart could be priced)
 *
 * `savings` is the backend's own discountAmount for the coupon's offer in this
 * quote (checkoutIncentives folds it across lines), or null when not supplied.
 */
export const couponResult = (quote, couponCode) => {
  const status = quote?.couponStatus;
  if (!couponCode || !status || status.code !== couponCode) return { state: 'unchecked' };

  if (status.applied) {
    const incentive = (quote.checkoutIncentives || []).find(
      (entry) => entry.source === 'store_offer' && entry.code === couponCode && entry.applied
    );
    const cartOffer = quote.appliedCartOffer?.code === couponCode ? quote.appliedCartOffer : null;
    const level = incentive?.level || cartOffer?.level || null;
    const amount = incentive?.discountAmount ?? cartOffer?.discountAmount ?? null;
    return {
      state: 'applied',
      freeDelivery: level === 'delivery',
      savings: typeof amount === 'number' && amount > 0 ? amount : null,
    };
  }

  const issue = (quote.issues || []).find(
    (entry) => entry.type === 'coupon_invalid' && (!entry.code || entry.code === couponCode)
  );
  return {
    state: 'refused',
    reason: status.reason || issue?.reason || null,
    message: couponRefusalMessage(status.reason || issue?.reason, issue?.message),
  };
};

/**
 * checkoutIncentives the quote evaluated but did not use — the only ones that
 * need explaining, since every applied one already has its own row or badge.
 * Returned as-is; the wording is the caller's.
 */
export const unusedIncentives = (quote) =>
  (quote?.checkoutIncentives || []).filter((entry) => entry && entry.applied === false && entry.label);
